import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import PropTypes from "prop-types";
import { ConversationContextProvider } from "./context/index.jsx";
import { types } from "./Hub/ConversationContent/types/index.jsx";
import {
  editUserConversation,
  inviteUserConversation,
  leaveConversation,
  renameGroupConversation,
  removeConversationParticipant,
  deleteConversation,
} from "../../store/actions/actions.conversations.jsx";
import { makeGetConversationById } from "../../store/selectors/selector.conversations.jsx";
import { makeGetUserConversationSelector } from "../../store/selectors/selector.userConversations.jsx";
import { getLoggedUserId, getLoggedUserType } from "../../store/selectors/selector.users.jsx";

export const ConversationProvider = ({ id, children }) => {
  const dispatch = useDispatch();

  const getConversation = useMemo(() => makeGetConversationById(id), [id]);
  const conversation = useSelector(getConversation);
  const { admins = [], class: classType } = conversation ?? {};
  const type = types[classType];

  const loggedUserId = useSelector(getLoggedUserId);
  const loggedUserType = useSelector(getLoggedUserType);

  const getUserConversation = useMemo(() => makeGetUserConversationSelector(id), [id]);
  const userConversation = useSelector(getUserConversation);
  const { is_muted = false, is_original_muted = false } = userConversation ?? {};
  const defaultMuted = is_muted || is_original_muted;
  const [isMuted, setIsMuted] = useState(defaultMuted);

  useUpdateEffect(() => {
    setIsMuted(defaultMuted);
  }, [defaultMuted]);

  const rename = useCallback(
    (values) => {
      dispatch(renameGroupConversation(id, values));
    },
    [dispatch, id]
  );

  const leave = useCallback(() => {
    dispatch(leaveConversation(id));
  }, [dispatch, id]);

  const invite = useCallback(
    (userIds) => {
      return dispatch(inviteUserConversation(id, userIds));
    },
    [dispatch, id]
  );

  const removeParticipant = useCallback(
    (participantId) => {
      return dispatch(removeConversationParticipant(id, participantId));
    },
    [dispatch, id]
  );

  const toggleMuted = useCallback(() => {
    const nextState = !isMuted;
    setIsMuted(nextState);
    dispatch(editUserConversation(id, { is_muted: nextState }));
  }, [dispatch, id, isMuted]);

  const handleDeleteConversation = useCallback(() => {
    dispatch(deleteConversation(id));
  }, [dispatch, id]);

  const canManage = useMemo(() => {
    return (loggedUserType === "Owner" || admins.includes(loggedUserId)) && type === "GroupConversation";
  }, [loggedUserType, admins, loggedUserId, type]);

  return (
    <ConversationContextProvider
      value={{
        conversation,
        rename,
        leave,
        isMuted,
        toggleMuted,
        canManage,
        invite,
        removeParticipant,
        loggedUserId,
        deleteConversation: handleDeleteConversation,
      }}
    >
      {children}
    </ConversationContextProvider>
  );
};

ConversationProvider.propTypes = {
  id: PropTypes.number.isRequired,
  children: PropTypes.node,
};
